// @ts-nocheck
import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardHeader, Box, Stack, TextField, Typography } from '@mui/material';
import Iconify from '../iconify';
import { useChart } from '../chart';
import { QueriesOverTime } from './types';

interface QueriesOverTimeChartProps {
  title: string;
  subheader?: string;
  data: QueriesOverTime[];
  onDateChange?: (startDate: string, endDate: string) => void;
}

export default function QueriesOverTimeChart({ title, subheader, data, onDateChange }: QueriesOverTimeChartProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const datesInitialized = React.useRef(false);

  // Initialize date pickers once from sorted min/max of first data arrival
  useEffect(() => {
    if (!datesInitialized.current && Array.isArray(data) && data.length > 0) {
      const sorted = data.map((d) => d.date).sort();
      setStartDate(sorted[0]);
      setEndDate(sorted[sorted.length - 1]);
      datesInitialized.current = true;
    }
  }, [data]);

  const safeData = Array.isArray(data) ? data : [];
  const chartLabels = safeData.map((d) => d.date);
  const chartSeries = [{ name: 'Queries', data: safeData.map((d) => d.queries), fill: 'gradient' }];

  const chartOptions = useChart({
    labels: chartLabels,
    xaxis: { type: 'datetime' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => (typeof y !== 'undefined' ? `${y.toFixed(0)} queries` : y),
      },
    },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 100] },
    },
  });

  return (
    <Card>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ pr: 2.5 }}
      >
        <CardHeader title={title} subheader={subheader} />
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: { xs: 2.5, sm: 0 },
            pb: { xs: 1.5, sm: 0 },
            gap: 1,
          }}
        >
          <Iconify icon="mdi:calendar-range" width={20} sx={{ color: 'text.secondary' }} />
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate || ''}
            onChange={(e) => {
              const val = e.target.value;
              setStartDate(val);
              if (val && endDate) onDateChange?.(val, endDate);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 155 } }}
          />
          <Typography variant="body2" sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}>
            —
          </Typography>
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate || ''}
            onChange={(e) => {
              const val = e.target.value;
              setEndDate(val);
              if (startDate && val) onDateChange?.(startDate, val);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 155 } }}
          />
        </Stack>
      </Stack>
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart type="area" series={chartSeries} options={chartOptions} height={320} />
      </Box>
    </Card>
  );
}
